import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { BACKEND } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Share2, AlertTriangle } from 'lucide-react';

const SYMBOLS = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'ADA', 'DOGE', 'AVAX', 'DOT', 'MATIC', 'LINK', 'LTC'];

export default function Futures() {
  const { symbol: symParam } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [symbol, setSymbol] = useState((symParam || 'BTC').toUpperCase());
  const [config, setConfig] = useState(null);
  const [mark, setMark] = useState(0);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [trades, setTrades] = useState([]);
  const [totals, setTotals] = useState({ upnl: 0, im: 0 });
  const [walletUsdt, setWalletUsdt] = useState(0);

  // Form
  const [side, setSide] = useState('long');
  const [orderType, setOrderType] = useState('market');
  const [marginMode, setMarginMode] = useState('isolated');
  const [leverage, setLeverage] = useState(10);
  const [quantity, setQuantity] = useState('');
  const [priceInput, setPriceInput] = useState('');
  const [tpPrice, setTpPrice] = useState('');
  const [slPrice, setSlPrice] = useState('');
  const [busy, setBusy] = useState(false);
  const [candles, setCandles] = useState([]);
  const [orderbook, setOrderbook] = useState(null);
  const [interval_, setIntervalTf] = useState('1h');
  const [futSettings, setFutSettings] = useState({ cross_wallet_mode: false, sound_alerts_optin: false });
  const [obHistory, setObHistory] = useState([]);   // volume samples for sparkline
  const [tape, setTape] = useState([]);
  const [soundPreset, setSoundPreset] = useState(() => localStorage.getItem('bg_sound_preset') || 'subtle');
  const prevPositions = useMemo(() => positions.map(p => p.position_id), [positions]);  // eslint-disable-line
  const [prevPosIds, setPrevPosIds] = useState([]);

  const loadTape = useCallback(async () => {
    try {
      const r = await api.get(`/futures/tape/${symbol}?limit=25`);
      setTape(r.data.trades || []);
    } catch { /* silent */ }
  }, [symbol]);
  useEffect(() => { loadTape(); const i = setInterval(loadTape, 1500); return () => clearInterval(i); }, [loadTape]);

  const cap = useMemo(() => config?.leverage_tiers?.[symbol] || config?.leverage_tiers?._DEFAULT || 25, [config, symbol]);
  useEffect(() => { if (leverage > cap) setLeverage(cap); }, [cap]);  // eslint-disable-line

  const loadKlines = useCallback(async () => {
    try {
      const r = await api.get(`/futures/klines/${symbol}?interval=${interval_}&limit=80`);
      setCandles(r.data.candles || []);
    } catch { /* silent */ }
  }, [symbol, interval_]);

  const loadOrderbook = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get(`/futures/orderbook/${symbol}?depth=12`);
      setOrderbook(r.data);
      // Track total book depth (asks + bids sum in USDT) for sparkline — keep last 60 samples
      const totalDepth = (r.data.bids || []).reduce((s, b) => s + b.cum_usdt, 0) +
                         (r.data.asks || []).reduce((s, a) => s + a.cum_usdt, 0);
      setObHistory(prev => {
        const next = [...prev, totalDepth];
        return next.length > 60 ? next.slice(-60) : next;
      });
    } catch { /* silent */ }
  }, [symbol, user]);

  // Reset volume-spark history on symbol change so we don't mix asset scales
  useEffect(() => { setObHistory([]); }, [symbol]);

  const loadFutSettings = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get('/futures/settings');
      setFutSettings(r.data);
    } catch { /* silent */ }
  }, [user]);
  useEffect(() => { loadFutSettings(); }, [loadFutSettings]);

  // Sound alerts — WebAudio synthesised beeps triggered on position lifecycle events
  const playBeep = useCallback((kind) => {
    if (!futSettings.sound_alerts_optin) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      // Preset tone banks
      const PRESETS = {
        subtle: {
          open: { f: 660, t: 'sine', gain: 0.10, dur: 0.25 },
          win:  { f: 880, t: 'sine', gain: 0.12, dur: 0.30 },
          loss: { f: 330, t: 'sine', gain: 0.10, dur: 0.30 },
          liquidated: { f: 200, t: 'sine', gain: 0.15, dur: 0.55 },
        },
        dramatic: {
          open: { f: 523, t: 'triangle', gain: 0.20, dur: 0.35 },
          win:  { f: 1046, t: 'square', gain: 0.22, dur: 0.50 },   // fanfare
          loss: { f: 150, t: 'sawtooth', gain: 0.22, dur: 0.60 },
          liquidated: { f: 90, t: 'sawtooth', gain: 0.28, dur: 0.90 },
        },
        meme: {
          open: { f: 987, t: 'square', gain: 0.18, dur: 0.18 },     // coin ping
          win:  { f: 1568, t: 'square', gain: 0.20, dur: 0.55 },   // level up
          loss: { f: 130, t: 'triangle', gain: 0.20, dur: 0.70 },  // sad trombone-ish
          liquidated: { f: 110, t: 'sawtooth', gain: 0.30, dur: 1.10 },
        },
      };
      const cfg = (PRESETS[soundPreset] || PRESETS.subtle)[kind] || PRESETS.subtle.open;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = cfg.f;
      o.type = cfg.t;
      g.gain.setValueAtTime(0.001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(cfg.gain, ctx.currentTime + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + cfg.dur);
      // Second oscillator for meme win (chord-y)
      if (soundPreset === 'meme' && kind === 'win') {
        const o2 = ctx.createOscillator();
        o2.connect(g); o2.frequency.value = cfg.f * 1.5; o2.type = 'square';
        o2.start(ctx.currentTime + 0.15); o2.stop(ctx.currentTime + cfg.dur);
      }
      o.start(); o.stop(ctx.currentTime + cfg.dur + 0.05);
    } catch { /* audio not permitted */ }
  }, [futSettings.sound_alerts_optin, soundPreset]);

  // Detect position open/close events and play sounds
  useEffect(() => {
    const currentIds = positions.map(p => p.position_id);
    // Newly opened
    const opened = currentIds.filter(id => !prevPosIds.includes(id));
    if (prevPosIds.length && opened.length) opened.forEach(() => playBeep('open'));
    // Closed since last poll — check trades for the latest close reason
    const closedIds = prevPosIds.filter(id => !currentIds.includes(id));
    if (prevPosIds.length && closedIds.length && trades.length) {
      trades.slice(0, 3).forEach(t => {
        if (closedIds.includes(t.position_id) && t.action === 'close') {
          if (t.reason === 'liquidation') playBeep('liquidated');
          else if (t.realized_pnl >= 0) playBeep('win');
          else playBeep('loss');
        }
      });
    }
    setPrevPosIds(currentIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [positions.length]);

  const toggleCross = async () => {
    const next = !futSettings.cross_wallet_mode;
    if (next && !window.confirm('Enable account-wide Cross Margin?\n\nAll your cross-mode positions will share the full USDT wallet as margin. Losses from one trade can be covered by unrealized profits of another — but a bad market move could liquidate multiple positions at once.')) return;
    try {
      await api.post('/futures/settings/cross_wallet', { enabled: next });
      setFutSettings(s => ({ ...s, cross_wallet_mode: next }));
      toast.success(next ? 'Cross-wallet ON' : 'Cross-wallet OFF');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const toggleSound = async () => {
    const next = !futSettings.sound_alerts_optin;
    try {
      await api.post('/futures/settings/sound_alerts', { enabled: next });
      setFutSettings(s => ({ ...s, sound_alerts_optin: next }));
      if (next) playBeep('open');   // Sample beep so user hears what it sounds like
      toast.success(next ? 'Sound alerts ON' : 'Sound alerts OFF');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  useEffect(() => { loadKlines(); const i = setInterval(loadKlines, 10000); return () => clearInterval(i); }, [loadKlines]);
  useEffect(() => { loadOrderbook(); const i = setInterval(loadOrderbook, 1000); return () => clearInterval(i); }, [loadOrderbook]);

  const loadConfig = async () => {
    const r = await api.get('/futures/config');
    setConfig(r.data);
  };

  const loadMark = useCallback(async () => {
    try {
      const r = await api.get(`/futures/mark_price/${symbol}`);
      setMark(r.data.mark_price);
    } catch { /* silent */ }
  }, [symbol]);

  const loadAll = useCallback(async () => {
    if (!user) return;
    try {
      const [p, o, t, w] = await Promise.all([
        api.get('/futures/positions'),
        api.get('/futures/orders'),
        api.get('/futures/trades?limit=15'),
        api.get('/wallet'),
      ]);
      setPositions(p.data.positions || []);
      setTotals({ upnl: p.data.total_unrealized_pnl || 0, im: p.data.total_initial_margin || 0 });
      setOrders(o.data.orders || []);
      setTrades(t.data.trades || []);
      const usdt = (w.data.balances || []).find(b => b.symbol === 'USDT');
      setWalletUsdt(usdt?.amount || 0);
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => { loadConfig(); }, []);
  useEffect(() => { loadMark(); const i = setInterval(loadMark, 3000); return () => clearInterval(i); }, [loadMark]);
  useEffect(() => { loadAll(); const i = setInterval(loadAll, 4000); return () => clearInterval(i); }, [loadAll]);

  // Countdown to next funding
  const [fundingCd, setFundingCd] = useState('--:--:--');
  useEffect(() => {
    if (!config?.next_funding_at) return;
    const tick = () => {
      const rem = Math.max(0, new Date(config.next_funding_at) - new Date());
      const h = String(Math.floor(rem / 3600000)).padStart(2, '0');
      const m = String(Math.floor((rem % 3600000) / 60000)).padStart(2, '0');
      const s = String(Math.floor((rem % 60000) / 1000)).padStart(2, '0');
      setFundingCd(`${h}:${m}:${s}`);
    };
    tick();
    const i = setInterval(tick, 1000);
    return () => clearInterval(i);
  }, [config?.next_funding_at]);

  const notional = useMemo(() => {
    const q = parseFloat(quantity) || 0;
    const p = orderType === 'market' ? mark : (parseFloat(priceInput) || 0);
    return q * p;
  }, [quantity, priceInput, orderType, mark]);
  const requiredMargin = notional / Math.max(1, leverage);
  const estFee = notional * (config?.taker_fee || 0.0004);
  // Risk / safe leverage
  const riskPct = walletUsdt > 0 ? (requiredMargin / walletUsdt) * 100 : 0;
  const RISK_THRESHOLD_PCT = 20;
  const safeLeverage = useMemo(() => {
    if (walletUsdt <= 0 || notional <= 0) return null;
    const target = walletUsdt * (RISK_THRESHOLD_PCT / 100);
    const suggested = Math.max(1, Math.ceil(notional / target));
    return Math.min(cap, suggested);
  }, [walletUsdt, notional, cap]);
  const isRisky = notional > 0 && riskPct > RISK_THRESHOLD_PCT;

  const backendUrl = BACKEND;
  const shareTrade = async (t) => {
    if (!user) return;
    const url = `${backendUrl}/api/futures/trades/${t.trade_id}/pnl_card.png`;
    const won = (t.realized_pnl || 0) >= 0;
    const text = `${won ? '🚀' : '💸'} Just ${won ? 'won' : 'closed'} a ${t.symbol} futures trade on BITGONODE — PnL ${t.realized_pnl >= 0 ? '+' : ''}${t.realized_pnl?.toFixed(2)} USDT.`;
    // Twitter/X compose URL
    const xUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'BITGONODE PnL', text, url }); return; } catch { /* fallthrough */ }
    }
    // Fallback: copy + open X
    try { await navigator.clipboard.writeText(`${text}\n${url}`); toast.success('Copied share text + PnL card link'); } catch { /* ignore */ }
    window.open(xUrl, '_blank', 'noopener,noreferrer');
  };

  const placeOrder = async () => {
    if (!user) { navigate('/login'); return; }
    const body = {
      symbol, side, type: orderType, margin_mode: marginMode,
      quantity: parseFloat(quantity), leverage,
    };
    if (orderType !== 'market') body.price = parseFloat(priceInput);
    if (tpPrice) body.tp_price = parseFloat(tpPrice);
    if (slPrice) body.sl_price = parseFloat(slPrice);
    setBusy(true);
    try {
      const r = await api.post('/futures/order', body);
      toast.success(orderType === 'market' ? `${side.toUpperCase()} ${symbol} opened @ $${r.data.entry_price?.toLocaleString()}` : `${orderType.toUpperCase()} order placed`);
      setQuantity(''); setPriceInput(''); setTpPrice(''); setSlPrice('');
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Order failed'); }
    finally { setBusy(false); }
  };

  const closePos = async (p, qty) => {
    if (qty && !window.confirm(`Close ${qty} ${p.symbol}?`)) return;
    try {
      await api.post(`/futures/position/${p.position_id}/close`, qty ? { quantity: qty } : {});
      toast.success(`Position closed`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Close failed'); }
  };
  const changeLev = async (p) => {
    const v = parseFloat(window.prompt(`New leverage (1-${cap}):`, String(p.leverage)));
    if (!v || v < 1 || v > cap) return;
    try { await api.post(`/futures/position/${p.position_id}/leverage`, { leverage: v }); toast.success('Leverage updated'); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const adjustMg = async (p) => {
    const v = parseFloat(window.prompt(`Margin delta (+ to add, - to remove) USDT:`, '0'));
    if (!v) return;
    try { await api.post(`/futures/position/${p.position_id}/margin`, { delta: v }); toast.success(`Margin ${v > 0 ? '+' : ''}${v} USDT`); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const setTpsl = async (p) => {
    const tp = window.prompt(`Take-profit price for ${p.symbol} (empty to clear):`, p.tp_price || '');
    const sl = window.prompt(`Stop-loss price (empty to clear):`, p.sl_price || '');
    const body = {};
    if (tp !== null) body.tp_price = tp === '' ? null : parseFloat(tp);
    if (sl !== null) body.sl_price = sl === '' ? null : parseFloat(sl);
    try { await api.post(`/futures/position/${p.position_id}/tpsl`, body); toast.success('TP/SL updated'); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const cancelOrder = async (o) => {
    try { await api.post(`/futures/order/${o.order_id}/cancel`); toast.success('Order cancelled'); loadAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const pctBtns = [0.25, 0.5, 0.75, 1];
  const fillQtyPct = (pct) => {
    const usable = Math.max(0, walletUsdt * pct);
    const px = orderType === 'market' ? mark : (parseFloat(priceInput) || mark);
    if (px > 0) setQuantity(((usable * leverage) / px).toFixed(6));
  };

  return (
    <div className="max-w-[1600px] mx-auto px-4 py-6 text-white" data-testid="futures-page">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="text-2xl font-bold tracking-tight">Futures</div>
        <div className="text-[10px] font-mono text-[#5E6673] uppercase">USDT-M · Perpetual · Simulated</div>
        <div className="ml-auto flex items-center gap-2">
          <Link to="/trade/BTC" className="text-xs text-[#848E9C] hover:text-white font-mono uppercase">Spot →</Link>
        </div>
      </div>

      {/* Top bar: symbol · mark · funding countdown */}
      <div className="border border-[#2B3139] bg-[#181A20] p-3 mb-3 flex flex-wrap items-center gap-4" data-testid="futures-topbar">
        <select
          value={symbol}
          onChange={e => setSymbol(e.target.value.toUpperCase())}
          className="bg-[#0B0E11] border border-[#2B3139] text-white px-3 py-1.5 font-mono text-sm rounded-sm"
          data-testid="futures-symbol-select"
        >
          {SYMBOLS.map(s => {
            const label = `${s}/USDT · max ${config?.leverage_tiers?.[s] || 25}×`;
            return <option key={s} value={s}>{label}</option>;
          })}
        </select>
        <div>
          <div className="text-[10px] font-mono text-[#848E9C] uppercase">Mark price</div>
          <div className="text-xl font-mono text-[#FCD535]" data-testid="futures-mark">${mark ? mark.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '—'}</div>
        </div>
        <div>
          <div className="text-[10px] font-mono text-[#848E9C] uppercase">Funding in</div>
          <div className="text-sm font-mono text-white" data-testid="futures-funding-cd">{fundingCd}</div>
          <div className="text-[10px] font-mono text-[#5E6673]">rate {((config?.funding_rate || 0) * 100).toFixed(3)}%</div>
        </div>
        <div>
          <div className="text-[10px] font-mono text-[#848E9C] uppercase">Wallet USDT</div>
          <div className="text-sm font-mono text-white">${walletUsdt.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
        </div>
        <div>
          <div className="text-[10px] font-mono text-[#848E9C] uppercase">Total UPnL</div>
          <div className={`text-sm font-mono ${totals.upnl >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`} data-testid="futures-total-upnl">
            {totals.upnl >= 0 ? '+' : ''}{totals.upnl.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <button
            onClick={toggleCross}
            className={`flex items-center gap-1.5 text-[10px] uppercase font-mono px-2 py-1 border ${futSettings.cross_wallet_mode ? 'border-[#FCD535] text-[#FCD535] bg-[#FCD535]/10' : 'border-[#2B3139] text-[#848E9C] hover:text-white'}`}
            data-testid="futures-cross-wallet-toggle"
            title="Account-wide cross margin — all cross positions share your USDT wallet as margin"
          >
            <span className={`w-1.5 h-1.5 rounded-full ${futSettings.cross_wallet_mode ? 'bg-[#FCD535]' : 'bg-[#5E6673]'}`} />
            Cross-Wallet {futSettings.cross_wallet_mode ? 'ON' : 'OFF'}
          </button>
          <button
            onClick={toggleSound}
            className={`flex items-center gap-1.5 text-[10px] uppercase font-mono px-2 py-1 border ${futSettings.sound_alerts_optin ? 'border-[#0ECB81] text-[#0ECB81] bg-[#0ECB81]/10' : 'border-[#2B3139] text-[#848E9C] hover:text-white'}`}
            data-testid="futures-sound-toggle"
            title="Play a subtle beep on position open / win / loss / liquidation"
          >
            <span>{futSettings.sound_alerts_optin ? '🔔' : '🔕'}</span>
            Sound {futSettings.sound_alerts_optin ? 'ON' : 'OFF'}
          </button>
          {futSettings.sound_alerts_optin && (
            <select
              value={soundPreset}
              onChange={e => { setSoundPreset(e.target.value); localStorage.setItem('bg_sound_preset', e.target.value); }}
              className="text-[10px] uppercase font-mono px-1.5 py-1 bg-[#0B0E11] border border-[#2B3139] text-[#B7BDC6]"
              data-testid="futures-sound-preset"
            >
              <option value="subtle">Subtle</option>
              <option value="dramatic">Dramatic</option>
              <option value="meme">Meme</option>
            </select>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-3">
        {/* LEFT: chart + book + positions + orders + trades */}
        <div className="space-y-3">
          {/* Candlestick chart + Order Book side-by-side */}
          <div className="grid grid-cols-1 md:grid-cols-[1fr_240px] gap-3">
            <div className="border border-[#2B3139] bg-[#181A20] p-3" data-testid="futures-chart-panel">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-xs uppercase font-mono text-[#B7BDC6]">{symbol}/USDT · Chart</div>
                <div className="ml-auto flex gap-1">
                  {['5m', '15m', '1h', '4h', '1d'].map(tf => (
                    <button
                      key={tf}
                      onClick={() => setIntervalTf(tf)}
                      className={`text-[10px] font-mono px-1.5 py-0.5 ${interval_ === tf ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#848E9C] hover:text-white'}`}
                      data-testid={`chart-tf-${tf}`}
                    >
                      {tf}
                    </button>
                  ))}
                </div>
              </div>
              <CandleChart candles={candles} />
            </div>

            <div className="border border-[#2B3139] bg-[#181A20] p-3 flex flex-col" data-testid="futures-orderbook-panel">
              <div className="text-xs uppercase font-mono text-[#B7BDC6] mb-2">Order Book</div>
              <OrderBook data={orderbook} history={obHistory} />
              <TradeTape trades={tape} symbol={symbol} />
            </div>
          </div>

          {/* Positions */}
          <div className="border border-[#2B3139] bg-[#181A20] p-3" data-testid="futures-positions-panel">
            <div className="text-xs uppercase font-mono text-[#B7BDC6] mb-2">Positions · {positions.length}</div>
            {positions.length === 0 ? (
              <div className="text-[#5E6673] text-sm py-6 text-center">No open positions</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-[11px] font-mono">
                  <thead className="text-[#848E9C] text-[10px] uppercase">
                    <tr className="border-b border-[#2B3139]">
                      <th className="text-left py-1.5 pr-2">Symbol</th>
                      <th className="text-left pr-2">Side · Lev</th>
                      <th className="text-right pr-2">Qty</th>
                      <th className="text-right pr-2">Entry</th>
                      <th className="text-right pr-2">Mark</th>
                      <th className="text-right pr-2">Liq</th>
                      <th className="text-right pr-2">UPnL / ROE</th>
                      <th className="text-right pr-2">Margin</th>
                      <th className="text-right pr-2">MR</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {positions.map(p => (
                      <tr key={p.position_id} className="border-b border-[#2B3139]/50 hover:bg-[#0B0E11]" data-testid={`position-${p.position_id}`}>
                        <td className="py-1.5 pr-2 text-white font-semibold">{p.symbol}</td>
                        <td className={`pr-2 uppercase ${p.side === 'long' ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                          {p.side === 'long' ? '▲ LONG' : '▼ SHORT'} · {p.leverage}× <span className="text-[#5E6673]">{p.margin_mode}</span>
                        </td>
                        <td className="text-right pr-2">{p.quantity}</td>
                        <td className="text-right pr-2 text-[#B7BDC6]">${p.entry_price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        <td className="text-right pr-2 text-[#FCD535]">${p.mark_price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        <td className="text-right pr-2 text-[#F6465D]">${p.liquidation_price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                        <td className={`text-right pr-2 ${p.unrealized_pnl >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                          {p.unrealized_pnl >= 0 ? '+' : ''}{p.unrealized_pnl?.toFixed(2)} <span className="text-[10px]">({p.roe_pct >= 0 ? '+' : ''}{p.roe_pct}%)</span>
                        </td>
                        <td className="text-right pr-2 text-[#B7BDC6]">${(p.initial_margin + (p.added_margin || 0)).toFixed(2)}</td>
                        <td className={`text-right pr-2 ${p.margin_ratio > 0.7 ? 'text-[#F6465D]' : 'text-[#B7BDC6]'}`}>{(p.margin_ratio * 100).toFixed(1)}%</td>
                        <td className="text-right whitespace-nowrap">
                          <button onClick={() => setTpsl(p)} title="TP/SL" className="text-[10px] px-1.5 py-0.5 border border-[#2B3139] text-[#B7BDC6] hover:text-white mr-1" data-testid={`tpsl-${p.position_id}`}>TP/SL</button>
                          {p.margin_mode === 'isolated' && <button onClick={() => adjustMg(p)} title="Margin" className="text-[10px] px-1.5 py-0.5 border border-[#2B3139] text-[#B7BDC6] hover:text-white mr-1" data-testid={`mg-${p.position_id}`}>±M</button>}
                          <button onClick={() => changeLev(p)} title="Leverage" className="text-[10px] px-1.5 py-0.5 border border-[#2B3139] text-[#B7BDC6] hover:text-white mr-1" data-testid={`lev-${p.position_id}`}>Lev</button>
                          <button onClick={() => closePos(p)} className="text-[10px] px-2 py-0.5 border border-[#F6465D]/50 text-[#F6465D] hover:bg-[#F6465D]/10" data-testid={`close-${p.position_id}`}>Close</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pending orders + trade history */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="border border-[#2B3139] bg-[#181A20] p-3" data-testid="futures-orders-panel">
              <div className="text-xs uppercase font-mono text-[#B7BDC6] mb-2">Pending Orders · {orders.length}</div>
              {orders.length === 0 ? <div className="text-[#5E6673] text-xs py-3 text-center">None</div> :
                orders.map(o => (
                  <div key={o.order_id} className="flex items-center justify-between border-b border-[#2B3139]/50 py-1.5 text-[11px] font-mono">
                    <div>
                      <span className={o.side === 'long' ? 'text-[#0ECB81]' : 'text-[#F6465D]'}>{o.side.toUpperCase()}</span>
                      <span className="text-white ml-2">{o.symbol}</span>
                      <span className="text-[#848E9C] ml-1">{o.type}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-[#B7BDC6]">{o.quantity} @ ${o.price?.toLocaleString()}</div>
                      <button onClick={() => cancelOrder(o)} className="text-[10px] text-[#F6465D] hover:underline" data-testid={`cancel-${o.order_id}`}>Cancel</button>
                    </div>
                  </div>
                ))
              }
            </div>
            <div className="border border-[#2B3139] bg-[#181A20] p-3" data-testid="futures-trades-panel">
              <div className="text-xs uppercase font-mono text-[#B7BDC6] mb-2">Recent Fills · {trades.length}</div>
              {trades.length === 0 ? <div className="text-[#5E6673] text-xs py-3 text-center">No fills yet</div> :
                trades.slice(0, 8).map(t => (
                  <div key={t.trade_id} className="flex items-center justify-between border-b border-[#2B3139]/50 py-1 text-[11px] font-mono">
                    <div className="flex-1 min-w-0 truncate">
                      <span className={t.action === 'open' ? 'text-[#0ECB81]' : 'text-[#B26BFF]'}>{t.action.toUpperCase()}</span>
                      <span className="text-white ml-1">{t.symbol}</span>
                      <span className="text-[#848E9C] ml-1">{t.quantity} @ ${t.price?.toLocaleString()}</span>
                    </div>
                    {t.realized_pnl != null && (
                      <div className={`ml-2 ${t.realized_pnl >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                        {t.realized_pnl >= 0 ? '+' : ''}{t.realized_pnl.toFixed(2)}
                      </div>
                    )}
                    {t.action === 'close' && (
                      <button
                        onClick={() => shareTrade(t)}
                        className={`ml-2 text-[10px] px-1.5 py-0.5 border ${t.realized_pnl >= 0 ? 'border-[#0ECB81]/50 text-[#0ECB81] hover:bg-[#0ECB81]/10' : 'border-[#2B3139] text-[#848E9C] hover:text-white'}`}
                        title={t.realized_pnl >= 0 ? 'Share this win' : 'Share this trade'}
                        data-testid={`share-trade-${t.trade_id}`}
                      >
                        <Share2 className="w-3 h-3 inline" />
                      </button>
                    )}
                  </div>
                ))
              }
            </div>
          </div>
        </div>

        {/* RIGHT: order form */}
        <div className="border border-[#2B3139] bg-[#181A20] p-3 h-fit" data-testid="futures-order-form">
          {/* Long / Short */}
          <div className="grid grid-cols-2 gap-1 mb-3" data-testid="futures-side-toggle">
            <button onClick={() => setSide('long')} className={`py-2 text-sm font-semibold ${side === 'long' ? 'bg-[#0ECB81] text-white' : 'bg-[#2B3139] text-[#B7BDC6]'}`} data-testid="side-long">
              <TrendingUp className="w-3.5 h-3.5 inline mr-1" /> LONG
            </button>
            <button onClick={() => setSide('short')} className={`py-2 text-sm font-semibold ${side === 'short' ? 'bg-[#F6465D] text-white' : 'bg-[#2B3139] text-[#B7BDC6]'}`} data-testid="side-short">
              <TrendingDown className="w-3.5 h-3.5 inline mr-1" /> SHORT
            </button>
          </div>

          {/* Order type + margin mode */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">Order type</div>
              <select value={orderType} onChange={e => setOrderType(e.target.value)} className="w-full bg-[#0B0E11] border border-[#2B3139] text-white px-2 py-1.5 text-sm rounded-sm" data-testid="futures-order-type">
                <option value="market">Market</option>
                <option value="limit">Limit</option>
                <option value="stop_market">Stop-Market</option>
                <option value="tp_market">TP-Market</option>
              </select>
            </div>
            <div>
              <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">Margin</div>
              <select value={marginMode} onChange={e => setMarginMode(e.target.value)} className="w-full bg-[#0B0E11] border border-[#2B3139] text-white px-2 py-1.5 text-sm rounded-sm" data-testid="futures-margin-mode">
                <option value="isolated">Isolated</option>
                <option value="cross">Cross</option>
              </select>
            </div>
          </div>

          {/* Leverage slider */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] uppercase font-mono text-[#848E9C]">Leverage</div>
              <div className="text-sm font-mono text-[#FCD535]" data-testid="futures-lev-display">{leverage}×</div>
            </div>
            <input
              type="range" min="1" max={cap} step="1"
              value={leverage} onChange={e => setLeverage(parseInt(e.target.value))}
              className="w-full accent-[#FCD535]"
              data-testid="futures-lev-slider"
            />
            <div className="grid grid-cols-5 gap-1 mt-1">
              {[...new Set([1, 5, 10, 25, cap])].slice(0, 5).map(v => (
                <button key={v} onClick={() => setLeverage(v)} className={`text-[10px] py-0.5 font-mono ${leverage === v ? 'bg-[#FCD535] text-[#0B0E11]' : 'bg-[#2B3139] text-[#B7BDC6]'}`} data-testid={`lev-preset-${v}`}>{v}×</button>
              ))}
            </div>
          </div>

          {/* Price (if not market) */}
          {orderType !== 'market' && (
            <div className="mb-2">
              <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">
                {orderType === 'limit' ? 'Limit price' : orderType === 'stop_market' ? 'Stop trigger' : 'TP trigger'}
              </div>
              <Input value={priceInput} onChange={e => setPriceInput(e.target.value)} placeholder={mark ? mark.toString() : '0'} type="number" className="bg-[#0B0E11] border-[#2B3139] text-white" data-testid="futures-price-input" />
            </div>
          )}

          {/* Quantity */}
          <div className="mb-2">
            <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">Quantity ({symbol})</div>
            <Input value={quantity} onChange={e => setQuantity(e.target.value)} placeholder="0.00" type="number" className="bg-[#0B0E11] border-[#2B3139] text-white" data-testid="futures-qty-input" />
            <div className="grid grid-cols-4 gap-1 mt-1">
              {pctBtns.map(p => (
                <button key={p} onClick={() => fillQtyPct(p)} className="text-[10px] py-0.5 bg-[#2B3139] text-[#B7BDC6] hover:bg-[#3B4149]" data-testid={`qty-pct-${p * 100}`}>
                  {p * 100}%
                </button>
              ))}
            </div>
          </div>

          {/* Optional TP / SL */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">Take Profit</div>
              <Input value={tpPrice} onChange={e => setTpPrice(e.target.value)} placeholder="—" type="number" className="bg-[#0B0E11] border-[#2B3139] text-white text-sm h-8" data-testid="futures-tp-input" />
            </div>
            <div>
              <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">Stop Loss</div>
              <Input value={slPrice} onChange={e => setSlPrice(e.target.value)} placeholder="—" type="number" className="bg-[#0B0E11] border-[#2B3139] text-white text-sm h-8" data-testid="futures-sl-input" />
            </div>
          </div>

          {/* Cost summary */}
          <div className="border-t border-[#2B3139] pt-2 mb-2 text-[11px] font-mono space-y-0.5">
            <div className="flex justify-between"><span className="text-[#848E9C]">Notional</span><span className="text-white">${notional.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-[#848E9C]">Required margin</span><span className="text-[#FCD535]">${requiredMargin.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-[#848E9C]">Est. taker fee</span><span className="text-[#B7BDC6]">${estFee.toFixed(2)}</span></div>
            {notional > 0 && (
              <div className="flex justify-between items-center pt-1">
                <span className="text-[#848E9C]">Risk of wallet</span>
                <span className={isRisky ? 'text-[#F6465D]' : 'text-[#0ECB81]'} data-testid="futures-risk-pct">
                  {riskPct.toFixed(1)}%
                </span>
              </div>
            )}
          </div>

          {isRisky && safeLeverage && safeLeverage !== leverage && (
            <div className="border border-[#F6465D]/40 bg-[#F6465D]/10 p-2 mb-2 text-[11px]" data-testid="futures-risk-warning">
              <div className="flex items-center gap-1 text-[#F6465D] font-semibold mb-1">
                <AlertTriangle className="w-3.5 h-3.5" /> High risk — {riskPct.toFixed(0)}% of your wallet
              </div>
              <div className="text-[#B7BDC6] mb-1">A single bad move on {leverage}× could liquidate a significant chunk of your bag.</div>
              <button
                onClick={() => setLeverage(safeLeverage)}
                className="w-full text-[11px] py-1 bg-[#FCD535] text-[#0B0E11] font-semibold hover:bg-[#F0B90B]"
                data-testid="futures-use-safe-leverage"
              >
                Use safe {safeLeverage}× (keeps risk ≤ 20%)
              </button>
            </div>
          )}

          <Button
            onClick={placeOrder}
            disabled={busy || !quantity || parseFloat(quantity) <= 0}
            className={`w-full font-semibold ${side === 'long' ? 'bg-[#0ECB81] hover:bg-[#0BA36B]' : 'bg-[#F6465D] hover:bg-[#D93B4E]'} text-white`}
            data-testid="futures-place-order"
          >
            {busy ? '…' : `Open ${side.toUpperCase()} ${symbol} · ${leverage}×`}
          </Button>

          {!user && (
            <Link to="/login" className="block text-center text-xs text-[#FCD535] mt-2 hover:underline">Log in to trade</Link>
          )}
        </div>
      </div>
    </div>
  );
}

// -------- Candlestick chart (inline SVG, no extra deps) --------
function CandleChart({ candles }) {
  if (!candles || candles.length < 2) {
    return <div className="h-56 grid place-items-center text-[#5E6673] text-xs">Loading chart…</div>;
  }
  const W = 640, H = 220, padL = 44, padR = 4, padT = 4, padB = 20;
  const chartW = W - padL - padR, chartH = H - padT - padB;
  const highs = candles.map(c => c.h); const lows = candles.map(c => c.l);
  const maxP = Math.max(...highs); const minP = Math.min(...lows);
  const range = Math.max(1e-9, maxP - minP);
  const step = chartW / candles.length;
  const cw = Math.max(2, step * 0.7);
  const y = p => padT + chartH - ((p - minP) / range) * chartH;
  const lastClose = candles[candles.length - 1].c;
  const firstOpen = candles[0].o;
  const upOverall = lastClose >= firstOpen;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 240 }}>
      {/* Grid */}
      {[0.25, 0.5, 0.75].map(f => (
        <line key={f} x1={padL} x2={W - padR} y1={padT + chartH * f} y2={padT + chartH * f} stroke="#2B3139" strokeWidth="0.5" />
      ))}
      {/* Price labels */}
      {[maxP, (maxP + minP) / 2, minP].map((p, i) => (
        <text key={i} x={0} y={padT + i * (chartH / 2) + 3} fill="#848E9C" fontSize="9" fontFamily="monospace">
          {p >= 100 ? p.toFixed(0) : p >= 1 ? p.toFixed(2) : p.toFixed(6)}
        </text>
      ))}
      {/* Candles */}
      {candles.map((c, i) => {
        const cx = padL + i * step + step / 2;
        const up = c.c >= c.o;
        const color = up ? '#0ECB81' : '#F6465D';
        return (
          <g key={i}>
            <line x1={cx} x2={cx} y1={y(c.h)} y2={y(c.l)} stroke={color} strokeWidth="1" />
            <rect
              x={cx - cw / 2}
              y={y(Math.max(c.o, c.c))}
              width={cw}
              height={Math.max(1, Math.abs(y(c.o) - y(c.c)))}
              fill={color}
            />
          </g>
        );
      })}
      {/* Last price line */}
      <line x1={padL} x2={W - padR} y1={y(lastClose)} y2={y(lastClose)} stroke="#FCD535" strokeDasharray="3,3" strokeWidth="0.6" />
      <rect x={W - padR - 62} y={y(lastClose) - 8} width="62" height="14" fill="#FCD535" />
      <text x={W - padR - 58} y={y(lastClose) + 3} fill="#0B0E11" fontSize="10" fontFamily="monospace">
        {lastClose >= 100 ? lastClose.toFixed(0) : lastClose.toFixed(4)}
      </text>
      {/* Overall delta */}
      <text x={padL + 4} y={H - 4} fontSize="10" fontFamily="monospace" fill={upOverall ? '#0ECB81' : '#F6465D'}>
        {upOverall ? '▲' : '▼'} {(((lastClose - firstOpen) / firstOpen) * 100).toFixed(2)}%
      </text>
    </svg>
  );
}

// -------- Order Book depth ladder (LIVE — ticks every second) --------
function OrderBook({ data, history = [] }) {
  if (!data) return <div className="text-[#5E6673] text-xs py-6 text-center">Loading book…</div>;
  const { bids, asks, mark_price, user_orders = [] } = data;
  const displayMid = mark_price;

  // Merge user's own limit orders into the price ladder — same-price levels
  // have their qty aggregated so users see how much they contribute to depth.
  const roundKey = p => Math.round(p * 100) / 100;
  const userAskMap = {}; const userBidMap = {};
  user_orders.forEach(o => {
    const k = roundKey(o.price);
    if (o.side === 'short') userAskMap[k] = (userAskMap[k] || 0) + o.quantity;
    else userBidMap[k] = (userBidMap[k] || 0) + o.quantity;
  });

  // Enrich rows with user contribution + total qty
  const mergedAsks = (asks || []).map(a => {
    const uq = userAskMap[roundKey(a.price)] || 0;
    return { ...a, user_qty: uq, total_qty: a.qty + uq };
  });
  const mergedBids = (bids || []).map(b => {
    const uq = userBidMap[roundKey(b.price)] || 0;
    return { ...b, user_qty: uq, total_qty: b.qty + uq };
  });

  // Recompute cumulative USDT using MERGED qtys for accurate depth bars
  let cumA = 0; mergedAsks.forEach(r => { cumA += r.total_qty * r.price; r.cum_usdt_merged = cumA; });
  let cumB = 0; mergedBids.forEach(r => { cumB += r.total_qty * r.price; r.cum_usdt_merged = cumB; });

  const maxCum = Math.max(
    ...mergedBids.map(b => b.cum_usdt_merged),
    ...mergedAsks.map(a => a.cum_usdt_merged),
    1,
  );
  const asksRev = [...mergedAsks].reverse().slice(0, 8);
  const bidsTop = mergedBids.slice(0, 8);
  const currentDepth = history.length ? history[history.length - 1] : 0;

  return (
    <div className="text-[10px] font-mono" data-testid="orderbook-inner">
      {/* Volume sparkline — total book depth over last minute */}
      {history.length >= 2 && (
        <VolumeSpark data={history} data-testid="orderbook-volume-spark" />
      )}
      <div className="flex items-center justify-between text-[9px] uppercase text-[#5E6673] pb-1">
        <span data-testid="orderbook-simulated-label">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#0ECB81] animate-pulse mr-1" /> Live · Simulated
        </span>
        {currentDepth > 0 && (
          <span className="text-[#848E9C]" data-testid="orderbook-total-depth">
            depth ${currentDepth >= 1e6 ? (currentDepth / 1e6).toFixed(2) + 'M' : (currentDepth / 1000).toFixed(0) + 'k'}
          </span>
        )}
      </div>
      <div className="grid grid-cols-[1fr_1fr] text-[#848E9C] pb-1 border-b border-[#2B3139]">
        <div>Price</div><div className="text-right">Size · Cum</div>
      </div>
      {/* Asks (red) */}
      {asksRev.map((a, i) => {
        const pct = (a.cum_usdt_merged / maxCum) * 100;
        return (
          <div key={`a${i}`} className={`relative grid grid-cols-[1fr_1fr] py-[1px] ${a.user_qty ? 'bg-[#FCD535]/10' : ''}`} data-testid={`ob-ask-${i}`}>
            <div className="absolute inset-y-0 right-0 bg-[#F6465D]/10" style={{ width: `${pct}%` }} />
            <div className={`relative z-10 ${a.user_qty ? 'text-[#FCD535] font-semibold' : 'text-[#F6465D]'}`}>
              {a.user_qty > 0 && '● '}{a.price.toLocaleString()}
            </div>
            <div className="relative z-10 text-right text-[#B7BDC6]">
              {a.total_qty.toFixed(4)}
              {a.user_qty > 0 && <span className="text-[#FCD535] ml-1" title="Includes your open order">·you {a.user_qty}</span>}
            </div>
          </div>
        );
      })}
      {/* Mark price row */}
      <div className="py-1 my-1 border-y border-[#2B3139] text-center">
        <span className="text-[#FCD535] font-semibold text-[13px]">${displayMid?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
        <span className="text-[#5E6673] ml-1">mark</span>
      </div>
      {/* Bids (green) */}
      {bidsTop.map((b, i) => {
        const pct = (b.cum_usdt_merged / maxCum) * 100;
        return (
          <div key={`b${i}`} className={`relative grid grid-cols-[1fr_1fr] py-[1px] ${b.user_qty ? 'bg-[#FCD535]/10' : ''}`} data-testid={`ob-bid-${i}`}>
            <div className="absolute inset-y-0 right-0 bg-[#0ECB81]/10" style={{ width: `${pct}%` }} />
            <div className={`relative z-10 ${b.user_qty ? 'text-[#FCD535] font-semibold' : 'text-[#0ECB81]'}`}>
              {b.user_qty > 0 && '● '}{b.price.toLocaleString()}
            </div>
            <div className="relative z-10 text-right text-[#B7BDC6]">
              {b.total_qty.toFixed(4)}
              {b.user_qty > 0 && <span className="text-[#FCD535] ml-1" title="Includes your open order">·you {b.user_qty}</span>}
            </div>
          </div>
        );
      })}
      {user_orders.length > 0 && (
        <div className="mt-2 pt-2 border-t border-[#2B3139] text-[#5E6673]">
          <span className="text-[#FCD535]">●</span> your open orders ({user_orders.length}) — aggregated into the book
        </div>
      )}
    </div>
  );
}

// -------- Trade Tape (scrolling recent trades feed) --------
function TradeTape({ trades, symbol }) {
  const fmtTime = at => {
    try {
      const d = new Date(at);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
    } catch { return '--:--:--'; }
  };
  // Aggregate buy vs sell notional over the last ~60 seconds of tape prints.
  // Powers a mini pressure bar so users can gauge intraday order-flow bias at a glance.
  const pressure = (() => {
    if (!trades || trades.length === 0) return { buy: 0, sell: 0, buyPct: 50, total: 0 };
    const cutoff = Date.now() - 60_000;
    let buy = 0, sell = 0;
    for (const t of trades) {
      const ts = t.at ? Date.parse(t.at) : 0;
      if (ts && ts < cutoff) continue;
      const notional = (Number(t.price) || 0) * (Number(t.qty) || 0);
      if (t.side === 'buy') buy += notional;
      else sell += notional;
    }
    const total = buy + sell;
    return { buy, sell, total, buyPct: total > 0 ? (buy / total) * 100 : 50 };
  })();
  // 5-minute heat trail: append a buyPct sample each time the parent hands us
  // a fresh tape (parent polls every 1.5s). We keep the last 200 samples ≈ 5 minutes.
  const [heatTrail, setHeatTrail] = useState([]);
  useEffect(() => {
    if (pressure.total <= 0) return;
    setHeatTrail(prev => {
      const next = [...prev, { at: Date.now(), buyPct: pressure.buyPct }];
      return next.length > 200 ? next.slice(-200) : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trades]);
  // Reset trail on symbol change so we don't mix symbols' pressure histories
  useEffect(() => { setHeatTrail([]); }, [symbol]);
  const fmtUsd = v => v >= 1e6 ? `$${(v / 1e6).toFixed(2)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(1)}k` : `$${v.toFixed(0)}`;
  return (
    <div className="mt-3 pt-3 border-t border-[#2B3139] flex-1 min-h-[160px]" data-testid="futures-tape-panel">
      <div className="flex items-center justify-between text-[9px] uppercase text-[#5E6673] font-mono pb-1">
        <span data-testid="tape-title">Trade Tape · {symbol}</span>
        <span className="text-[#0ECB81]"><span className="inline-block w-1 h-1 rounded-full bg-[#0ECB81] animate-pulse mr-1" />live</span>
      </div>
      {pressure.total > 0 && (
        <div className="mb-1.5" data-testid="tape-pressure">
          <div className="flex items-center justify-between text-[8px] uppercase font-mono text-[#5E6673] mb-0.5">
            <span className="text-[#0ECB81]" data-testid="tape-pressure-buy">▲ {pressure.buyPct.toFixed(0)}% · {fmtUsd(pressure.buy)}</span>
            <span className="text-[#848E9C]">60s pressure</span>
            <span className="text-[#F6465D]" data-testid="tape-pressure-sell">{fmtUsd(pressure.sell)} · {(100 - pressure.buyPct).toFixed(0)}% ▼</span>
          </div>
          <div className="relative h-1.5 bg-[#2B3139] overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-[#0ECB81]"
              style={{ width: `${pressure.buyPct}%` }}
              data-testid="tape-pressure-bar-buy"
            />
            <div
              className="absolute inset-y-0 right-0 bg-[#F6465D]"
              style={{ width: `${100 - pressure.buyPct}%` }}
              data-testid="tape-pressure-bar-sell"
            />
          </div>
          {heatTrail.length >= 2 && (
            <PressureHeatTrail trail={heatTrail} />
          )}
        </div>
      )}
      <div className="grid grid-cols-[1fr_1fr_1fr] text-[#848E9C] text-[9px] uppercase pb-1 border-b border-[#2B3139]">
        <div>Time</div>
        <div className="text-right">Price</div>
        <div className="text-right">Size</div>
      </div>
      <div className="max-h-[220px] overflow-y-auto text-[10px] font-mono" data-testid="tape-list">
        {trades.length === 0 && <div className="text-[#5E6673] py-3 text-center">Loading tape…</div>}
        {trades.map(t => (
          <div key={t.id} className="grid grid-cols-[1fr_1fr_1fr] py-[1px]" data-testid={`tape-row-${t.id}`}>
            <div className="text-[#848E9C]">{fmtTime(t.at)}</div>
            <div className={`text-right ${t.side === 'buy' ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
              {t.price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              {t.kind === 'real' && <span className="ml-1 text-[8px] text-[#FCD535]" title="Real user trade">●</span>}
            </div>
            <div className="text-right text-[#B7BDC6]">{t.qty}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
function VolumeSpark({ data }) {
  const W = 220, H = 26, pad = 1;
  const minV = Math.min(...data), maxV = Math.max(...data);
  const range = Math.max(1, maxV - minV);
  const stepX = (W - pad * 2) / Math.max(1, data.length - 1);
  const y = v => H - pad - ((v - minV) / range) * (H - pad * 2);
  const points = data.map((v, i) => `${pad + i * stepX},${y(v)}`).join(' ');
  const last = data[data.length - 1];
  const first = data[0];
  const trendUp = last >= first;
  const color = trendUp ? '#0ECB81' : '#F6465D';
  return (
    <div className="mb-1" data-testid="orderbook-volume-spark">
      <div className="flex items-center justify-between text-[9px] text-[#5E6673] font-mono">
        <span>Book depth · 60s</span>
        <span className={trendUp ? 'text-[#0ECB81]' : 'text-[#F6465D]'}>
          {trendUp ? '▲' : '▼'} {(((last - first) / Math.max(1, first)) * 100).toFixed(1)}%
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-6">
        <polyline fill="none" stroke={color} strokeWidth="1.2" points={points} />
        {data.length > 1 && (
          <polygon
            fill={color + '22'}
            points={`${pad},${H - pad} ${points} ${pad + (data.length - 1) * stepX},${H - pad}`}
          />
        )}
      </svg>
    </div>
  );
}


// -------- Pressure Heat Trail (rolling 5-min buy/sell bias strip) --------
function PressureHeatTrail({ trail }) {
  // Each sample = { at, buyPct }. Renders 200 vertical bars whose color intensity
  // maps buyPct (0=full red, 50=neutral, 100=full green). Newest sample on the right.
  const W = 200, H = 14;
  const barW = W / 200;
  const avg = trail.reduce((a, s) => a + s.buyPct, 0) / trail.length;
  const trend = avg >= 50 ? 'bullish' : 'bearish';
  const trendColor = avg >= 50 ? '#0ECB81' : '#F6465D';
  return (
    <div className="mt-1" data-testid="tape-heat-trail">
      <div className="flex items-center justify-between text-[8px] uppercase font-mono text-[#5E6673] mb-0.5">
        <span>5m trail</span>
        <span className={trend === 'bullish' ? 'text-[#0ECB81]' : 'text-[#F6465D]'} data-testid="tape-heat-trail-avg">
          avg {avg.toFixed(0)}% · <span style={{ color: trendColor }}>{trend}</span>
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 14 }} preserveAspectRatio="none">
        {/* neutral center line */}
        <line x1="0" x2={W} y1={H / 2} y2={H / 2} stroke="#2B3139" strokeWidth="0.3" />
        {trail.map((s, i) => {
          // Right-align newest sample: last item at x = W - barW
          const x = W - (trail.length - i) * barW;
          const bias = s.buyPct - 50; // -50..+50
          const intensity = Math.min(1, Math.abs(bias) / 50);
          const color = bias >= 0 ? '#0ECB81' : '#F6465D';
          return (
            <rect
              key={i}
              x={x}
              y={0}
              width={Math.max(0.5, barW - 0.2)}
              height={H}
              fill={color}
              opacity={0.15 + intensity * 0.75}
            />
          );
        })}
      </svg>
    </div>
  );
}
